import { useState, } from 'react';
import { useParams, } from 'react-router-dom';
import { useFirstMountState, } from 'react-use';
import {
    Alert,
    Box,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Modal,
    Skeleton,
    Stack,
    Tooltip,
    Typography, } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RuleFolderIcon from '@mui/icons-material/RuleFolder';
import RemoveIcon from '@mui/icons-material/Remove';
import FolderIcon from '@mui/icons-material/Folder';
import { useApp, useAuth, useCollection, useFirestore, } from '../../hooks';

const FoldersList = () => {
    const { user, } = useAuth();
    const { id, } = useParams();
    const { documents, isPending, error, } = useCollection({
        col: 'folders',
        _query: [['uid', '==', user.uid]],
        _orderBy: ['createdAt', 'desc'],
    });

    const { updateArrayDocument: updateArrayFolders, response: { isPending: isPendingFirestoreFolders, } } = useFirestore('folders');
    const { updateArrayDocument: updateArraySets, response: { isPending: isPendingFirestoreSets, } } = useFirestore('sets');

    const isFirstMount = useFirstMountState();

    return (
        isFirstMount
            ? null
            : isPending
            ? <Box sx={{mt: -3,}}>
                {[1, 2, 3, 4, 5].map((value) => {
                    return (
                        <Skeleton
                            key={value}
                            animation='wave' sx={{
                            height: ['90px', '104px', '112px'],
                            mb: [-3.45, -4],
                            width: '100%',
                        }} />
                    )
                })}
            </Box>
            : error
                ? <Alert severity='error' variant='filled' sx={{mb: [4, 5, 6, 7]}}>{error.message}</Alert>
                : documents && Array.isArray(documents) && documents.length
                    ? <List disablePadding>
                        {
                            documents.map((doc, index) => {
                                const isIncludesSetId = doc.setsIds?.includes(id);

                                return (
                                    <ListItem
                                        key={doc.id}
                                        sx={{
                                            mb: documents.length - 1 === index ? 0 : [1, 1.25, 1.5, 1.75],
                                            p: 0,
                                        }}>
                                        <Stack direction='row' sx={{width: '100%'}}>
                                            <ListItemButton
                                                sx={{
                                                    border: 1,
                                                    borderColor: 'borderColor.main',
                                                    cursor: 'default',
                                                    flexGrow: 1,
                                                    mr: 2,
                                                    px: [1.25, 1.5, 1.75, 2],
                                                    py: [0.5, 0.75, 1],
                                                    '&:hover': {
                                                        bgcolor: 'transparent',
                                                    },
                                                }}>
                                                <ListItemIcon sx={{ minWidth: 'auto', mr: [1.25, 1.5, 1.75, 2], }}>
                                                    <FolderIcon sx={{
                                                        color: 'text.primary',
                                                        fontSize: [21, 23, 25, 27],
                                                    }} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={doc.title}
                                                    secondary={doc.description}
                                                    sx={{
                                                        '& .MuiListItemText-primary': {
                                                            color: 'text.primary',
                                                            display: 'block',
                                                            fontSize: [12, 13, 14],
                                                            overflowWrap: 'anywhere',
                                                        },
                                                        '& .MuiListItemText-secondary': {
                                                            fontSize: [11, 12, 13],
                                                            overflowWrap: 'anywhere',
                                                        },
                                                    }}
                                                />
                                            </ListItemButton>
                                            <Tooltip title={(isIncludesSetId) ? 'Удалить сет из папки' : 'Добавить сет в папку'} placement='top' arrow>
                                                <Box component='span'>
                                                    <IconButton
                                                        disabled={isPendingFirestoreFolders || isPendingFirestoreSets}
                                                        onClick={() => {
                                                            if (isIncludesSetId) {
                                                                updateArrayFolders(doc.id, id, 'setsIds', 'delete');
                                                                updateArraySets(id, doc.id, 'foldersIds', 'delete');
                                                            } else {
                                                                updateArrayFolders(doc.id, id, 'setsIds', 'add');
                                                                updateArraySets(id, doc.id, 'foldersIds', 'add');
                                                            }
                                                        }}
                                                        sx={{
                                                            border: 1,
                                                            borderColor: 'borderColor.main',
                                                            borderRadius: 0,
                                                            height: '100%',
                                                            minHeight: 48,
                                                            p: [0.25, 0.5, 0.75, 1],
                                                            width: 48,
                                                            '&:hover': {
                                                                borderColor: 'secondary.main',
                                                                '& .MuiSvgIcon-root': {
                                                                    color: 'secondary.main',
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        { (isIncludesSetId) ? <RemoveIcon /> : <AddIcon /> }
                                                    </IconButton>
                                                </Box>
                                            </Tooltip>
                                        </Stack>
                                    </ListItem>
                                );
                            })
                        }
                    </List>
                    : <Typography sx={{fontSize: 14,}}>Вы еще не создали ни одной папки</Typography>
    );
};

const AddSetToFolder = ({ sx = [], ...props }) => {
    const [openModal, setOpenModal] = useState(false);
    const { newFolderModalOpen, } = useApp();

    const handleModalOpen = () => {
        setOpenModal(true);
    };
    const handleModalClose = () => {
        setOpenModal(false);
    };

    return (
        <Box
            {...props}
            sx={[
            ...(Array.isArray(sx) ? sx : [sx]),
        ]}>
            <Button
                fullWidth
                startIcon={<RuleFolderIcon />}
                onClick={handleModalOpen}
                sx={{
                    border: 1,
                    borderColor: (theme) => theme.palette.mode === 'light' ? 'borderColor.main' : 'common.pattensBlue',
                    color: 'text.primary',
                    fontSize: [20, 22, 24, 27],
                    fontWeight: 700,
                    p: [2.25, 2.5, 2.75, 3],
                    textTransform: 'none',
                    '&:hover': {
                        borderColor: 'secondary.main',
                        color: 'secondary.main',
                        '& .MuiSvgIcon-root': {
                            color: 'secondary.main',
                        },
                    },
                    '& .MuiSvgIcon-root': {
                        color: 'text.primary',
                        display: ['none', 'block'],
                        fontSize: [20, 24, 28, 32],
                        fontWeight: 700,
                        mr: 0.5,
                    },
                }}>
                Добавить сет в папку или удалить
            </Button>
            <Modal
                aria-labelledby='add-set-to-folder-modal-title'
                open={openModal}
                onClose={handleModalClose}
            >
                <Box sx={{ maxHeight: '80%', width: ['95%', '80%', '70%', '50%'], }}>
                    <Typography
                        id='add-set-to-folder-modal-title'
                        component='h2'
                        sx={{
                            fontSize: [24, 25, 26, 27],
                            fontWeight: 700,
                            mb: [3, 4, 4.5, 5],
                        }}>Добавьте сет в папку или удалите</Typography>
                    <Button
                        fullWidth
                        startIcon={<AddIcon sx={{fontSize: [15, 16, 17, 18],}} />}
                        onClick={newFolderModalOpen}
                        sx={{
                            border: 1,
                            borderColor: 'borderColor.main',
                            color: 'text.primary',
                            fontSize: [15, 16, 17, 18],
                            mb: [2.5, 3, 3.5, 4],
                            py: [1.25, 1.5, 1.75, 2],
                            '&:hover': {
                                borderColor: 'secondary.main',
                                color: 'secondary.main',
                            },
                        }}
                    >Создать новую папку</Button>
                    <FoldersList />
                </Box>
            </Modal>
        </Box>
    )
};

export default AddSetToFolder;